/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';

import { AircraftResponse } from '../models/aircraft-response';
import { CreateAircraftRequest } from '../models/create-aircraft-request';
import { GenericResponse } from '../models/generic-response';
import { GetAircraftResponse } from '../models/get-aircraft-response';
import { UpdateAircraftRequest } from '../models/update-aircraft-request';

/**
 * Aircraft APIs
 */
@Injectable({ providedIn: 'root' })
export class AircraftService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `getAllAircraft()` */
  static readonly GetAllAircraftPath = '/api/v1/aircrafts';

  /**
   * Get aircraft list.
   *
   * Returns all aircrafts registered as pages.
   * The results will be filtered, sorted based on the provided queries.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getAllAircraft()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllAircraft$Response(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a string search to the following fields:
       * - tailName
       * - type
       */
      search?: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GetAircraftResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      AircraftService.GetAllAircraftPath,
      'get'
    );
    if (params) {
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
      rb.query('search', params.search, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GetAircraftResponse>;
        })
      );
  }

  /**
   * Get aircraft list.
   *
   * Returns all aircrafts registered as pages.
   * The results will be filtered, sorted based on the provided queries.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getAllAircraft$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllAircraft(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a string search to the following fields:
       * - tailName
       * - type
       */
      search?: string;
    },
    context?: HttpContext
  ): Observable<GetAircraftResponse> {
    return this.getAllAircraft$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<GetAircraftResponse>): GetAircraftResponse =>
          r.body
      )
    );
  }

  /** Path part for operation `createAircraft()` */
  static readonly CreateAircraftPath = '/api/v1/aircrafts';

  /**
   * Create new aircraft.
   *
   * Registers a new Aircraft to the backend.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createAircraft()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createAircraft$Response(
    params: {
      body: CreateAircraftRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<AircraftResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      AircraftService.CreateAircraftPath,
      'post'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<AircraftResponse>;
        })
      );
  }

  /**
   * Create new aircraft.
   *
   * Registers a new Aircraft to the backend.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createAircraft$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createAircraft(
    params: {
      body: CreateAircraftRequest;
    },
    context?: HttpContext
  ): Observable<AircraftResponse> {
    return this.createAircraft$Response(params, context).pipe(
      map((r: StrictHttpResponse<AircraftResponse>): AircraftResponse => r.body)
    );
  }

  /** Path part for operation `getAircraftById()` */
  static readonly GetAircraftByIdPath = '/api/v1/aircrafts/{id}';

  /**
   * Get aircraft by id.
   *
   * Fetches details of an aircraft.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getAircraftById()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAircraftById$Response(
    params: {
      /**
       * The aircraft's id
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<AircraftResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      AircraftService.GetAircraftByIdPath,
      'get'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<AircraftResponse>;
        })
      );
  }

  /**
   * Get aircraft by id.
   *
   * Fetches details of an aircraft.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getAircraftById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAircraftById(
    params: {
      /**
       * The aircraft's id
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<AircraftResponse> {
    return this.getAircraftById$Response(params, context).pipe(
      map((r: StrictHttpResponse<AircraftResponse>): AircraftResponse => r.body)
    );
  }

  /** Path part for operation `deleteAircraft()` */
  static readonly DeleteAircraftPath = '/api/v1/aircrafts/{id}';

  /**
   * Delete aircraft by id.
   *
   * Marks an existing aircraft as deleted.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteAircraft()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteAircraft$Response(
    params: {
      /**
       * The aircraft's id that needs to be deleted
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GenericResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      AircraftService.DeleteAircraftPath,
      'delete'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GenericResponse>;
        })
      );
  }

  /**
   * Delete aircraft by id.
   *
   * Marks an existing aircraft as deleted.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deleteAircraft$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteAircraft(
    params: {
      /**
       * The aircraft's id that needs to be deleted
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<GenericResponse> {
    return this.deleteAircraft$Response(params, context).pipe(
      map((r: StrictHttpResponse<GenericResponse>): GenericResponse => r.body)
    );
  }

  /** Path part for operation `updateAircraft()` */
  static readonly UpdateAircraftPath = '/api/v1/aircrafts/{id}';

  /**
   * Update aircraft by id.
   *
   * Updates an existing aircrafts details, or else returns a 404 error.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateAircraft()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateAircraft$Response(
    params: {
      /**
       * The aircraft's id that needs to be updated
       */
      id: number;
      body: UpdateAircraftRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<AircraftResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      AircraftService.UpdateAircraftPath,
      'patch'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<AircraftResponse>;
        })
      );
  }

  /**
   * Update aircraft by id.
   *
   * Updates an existing aircrafts details, or else returns a 404 error.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateAircraft$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateAircraft(
    params: {
      /**
       * The aircraft's id that needs to be updated
       */
      id: number;
      body: UpdateAircraftRequest;
    },
    context?: HttpContext
  ): Observable<AircraftResponse> {
    return this.updateAircraft$Response(params, context).pipe(
      map((r: StrictHttpResponse<AircraftResponse>): AircraftResponse => r.body)
    );
  }
}
